import { AxiosResponse } from 'axios';

import { selectCompanyId, selectUserId } from '@/redux/features/authSlice';
import { selectOnboardingPageTitle } from '@/redux/features/onboardingSlice';
import { getStore } from '@/redux/store';
import { utilities } from '@/utils/utilities';

import { Env } from '../../../environments';
import TrackingRequests from '../../requests/tracking.requests';
import {
  AnalyticsPagePayload,
  AnalyticsTrackProps,
  OnboardingEvent,
  TrackEventApiPayload,
  TrackExperimentProps,
  TrackPageApiPayload,
} from '../../types';
import cookieUtils from '../../utils/cookies.util';
import DeviceAndGeoDetector from '../../utils/device-detector.utils';

import { AmplitudeService } from './amplitude.service';

export interface ITrackService {
  trackPage: (page: string, data: AnalyticsPagePayload) => Promise<void | AxiosResponse>;
  trackEvent: (event: string, data: AnalyticsTrackProps) => Promise<void | AxiosResponse>;
}

export class ConsoleTrackService implements ITrackService {
  public trackPage(page: string, data: AnalyticsPagePayload) {
    // eslint-disable-next-line no-console
    console.debug('--TRACK PAGE--', '/ NAME:', page, '/ DATA:', data);
    return Promise.resolve();
  }

  public trackEvent(event: string, data: AnalyticsTrackProps) {
    // eslint-disable-next-line no-console
    console.debug('--TRACK EVENT--', '/ NAME:', event, '/ DATA:', data);
    return Promise.resolve();
  }
}

export class ServerTrackService implements ITrackService {
  public async trackEvent(event: string, trackingProps: AnalyticsTrackProps) {
    const { category, ...restProps } = trackingProps;
    const userId = selectUserId(getStore().getState());

    // Get geo properties for tracking
    const geoProperties = await DeviceAndGeoDetector.getGeoTrackingProperties();

    // Track to Segment
    global.analytics.track(event, {
      anonymousId: utilities.uuid(),
      userId,
      category,
      ...restProps,
      ...geoProperties,
    });

    // Track to Amplitude
    AmplitudeService.track(event, {
      category,
      ...restProps,
      ...geoProperties,
    });

    // Track to Backend API (geoProperties already fetched above)
    const apiPayload: TrackEventApiPayload = {
      anonymousId: utilities.uuid(),
      userId,
      segmentanonymousid: utilities.segmentAnonymousId(),
      event,
      type: 'track',
      category,
      properties: {
        ...restProps,
        ...geoProperties,
      },
      context: {
        ...this.getContext(),
      },
    };

    return TrackingRequests.postTracking(apiPayload);
  }

  public async trackPage(page: string, trackingData: AnalyticsPagePayload) {
    const userId = selectUserId(getStore().getState());

    // Get geo properties for tracking
    const geoProperties = await DeviceAndGeoDetector.getGeoTrackingProperties();

    // Track to Segment
    global.analytics.page(trackingData.category, page, {
      anonymousId: utilities.uuid(),
      userId,
      ...trackingData.properties,
      ...geoProperties,
      path: globalThis.location.pathname,
      referrer: globalThis.document.referrer,
      search: globalThis.location.search,
      title: globalThis.document.title,
      url: globalThis.location.href,
    });

    // Track to Amplitude (as page view event)
    AmplitudeService.track('[Amplitude] Page Viewed', {
      page_name: page,
      page_category: trackingData.category,
      ...trackingData.properties,
      ...geoProperties,
      path: globalThis.location.pathname,
      referrer: globalThis.document.referrer,
      search: globalThis.location.search,
      title: globalThis.document.title || selectOnboardingPageTitle(getStore().getState()),
      url: globalThis.location.href,
    });

    // Track to Backend API (geoProperties already fetched above)
    const apiPayload: TrackPageApiPayload = {
      anonymousId: utilities.uuid(),
      userId,
      segmentanonymousid: utilities.segmentAnonymousId(),
      type: 'page',
      properties: {
        ...trackingData.properties,
        ...geoProperties,
        path: globalThis.location.pathname,
        referrer: globalThis.document.referrer,
        search: globalThis.location.search,
        title: globalThis.document.title || selectOnboardingPageTitle(getStore().getState()),
        url: globalThis.location.href,
      },
      category: trackingData.category,
      name: page,
      context: {
        ...this.getContext(),
      },
    };

    return TrackingRequests.postTracking(apiPayload);
  }

  private getContext() {
    return {
      campaign: cookieUtils.getUTMs(),
      groupId: selectCompanyId(getStore().getState()),
      page: {
        path: globalThis.location.pathname,
        referrer: globalThis.document.referrer,
        search: globalThis.location.search,
        title: globalThis.document.title,
        url: globalThis.location.href,
      },
    };
  }
}

export class TrackService {
  private static _instance: TrackService;

  trackingInstance: ITrackService;

  constructor() {
    this.trackingInstance = process.env.NEXT_PUBLIC_VERCEL_ENV === Env.Development
      ? new ConsoleTrackService()
      : new ServerTrackService();
  }

  static getInstance(): TrackService {
    if (!this._instance) {
      this._instance = new TrackService();
    }

    return this._instance;
  }

  trackPage = (page: string, trackProps: AnalyticsPagePayload) =>
    this.trackingInstance.trackPage(page, trackProps);

  trackEvent = (event: string, trackProps: AnalyticsTrackProps) =>
    this.trackingInstance.trackEvent(event, trackProps);

  trackOnboardingEvent = (event: OnboardingEvent, properties: Record<string, unknown>) =>
    this.trackEvent(event, {
      category: 'Onboarding',
      ...properties,
    });

  /**
   * Sends a `track` call to both AJS and Node with event name set to `Experiment Viewed`. It only makes
   * the tracking call when the experiment cookie is not set.
   *
   * @param props The properties that are sent along with the event name. This should always include
   *  at least the fields from {@link ExperimentProps}.
   */
  trackExperiment(props: TrackExperimentProps) {
    const trackedName = `tracked_${ props.experimentid.replace('-', '') }`;
    const trackedCookie = cookieUtils.getCookie(trackedName);

    if (trackedCookie !== 'true') {
      cookieUtils.setCookie(trackedName, 'true');

      return this.trackingInstance.trackEvent('Experiment Viewed', {
        ...props,
        trackedon: globalThis.location.href,
      });
    }
  }
}

export const injectTrackingService = () => TrackService.getInstance();
